import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  LinearProgress,
  Link,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useOutletContext } from 'react-router-dom';

import { Button } from '../../../../components/core';
import { useMerchant } from '../../../../contexts/MerchantContext';
import { useMerchantAwareNavigate } from '../../../../hooks/useMerchantAwareNavigate';
import type { AgentHubOutletContext } from '../../agentHubOutletContext';
import {
  useCreateOnboardingMutation,
  useGetAgenticVoiceProfilesQuery,
  useGetMerchantQuery,
  usePostAgenticSettingsMutation,
  useUpdateMerchantMutation,
} from '../../../../store/modules/merchant/api';
import { useOnboardingDraft } from '../OnboardingDraftContext';
import { executeOnboardingPublish, type OnboardingPublishResult } from '../publish';
import { getOnboardingPlatform, SQUARESPACE_PLATFORM_ID } from '../platforms';
import { useSquarespaceStoreLink } from '../useSquarespaceStoreLink';

const SHOPIFY_ONBOARDING_QUEUED_COMPLETED_STEP = 'Shopify onboarding added to the queue.';
const BIGCOMMERCE_ONBOARDING_QUEUED_COMPLETED_STEP = 'BigCommerce onboarding added to the queue.';

const PUBLISH_LAUNCH_STEPS = [
  'Validating agent configuration',
  'Saving personality and voice settings',
  'Applying guardrails and permissions',
] as const;

const PUBLISH_SUCCESS_HEADLINE =
  'Your agent configurations are saved, but the agent is not live yet';

const PUBLISH_SUCCESS_COMPLETED_STEPS = ['Agent configuration saved'] as const;

const PUBLISH_LAUNCH_STEP_DELAY_MS = 700;
const PUBLISH_SUCCESS_TRANSITION_DELAY_MS = 1000;

type LaunchStepStatus = 'pending' | 'in_progress' | 'complete';

type ModalPhase = 'launching' | 'success' | 'error';

const noop = () => {};

type Props = {
  open: boolean;
  onClose: () => void;
  onPublishSuccess?: () => void;
};

const getLaunchStepStatus = (
  stepIndex: number,
  activeIndex: number,
  launchComplete: boolean,
): LaunchStepStatus => {
  if (launchComplete || stepIndex < activeIndex) {
    return 'complete';
  }
  if (stepIndex === activeIndex) {
    return 'in_progress';
  }
  return 'pending';
};

const launchRowSx = (status: LaunchStepStatus) => {
  if (status === 'complete') {
    return {
      border: '1px solid',
      borderColor: 'success.main',
      bgcolor: 'success.light',
      color: 'text.primary',
    };
  }
  if (status === 'in_progress') {
    return {
      border: '1px solid',
      borderColor: 'warning.main',
      bgcolor: 'warning.light',
      color: 'text.primary',
    };
  }
  return {
    border: '1px dashed',
    borderColor: 'border.main',
    bgcolor: 'background.paper',
    color: 'text.disabled',
  };
};

export const PublishLaunchModal = ({ open, onClose, onPublishSuccess }: Props): JSX.Element => {
  const navigate = useMerchantAwareNavigate();
  const outletContext = useOutletContext<AgentHubOutletContext | null>();
  const setSuppressOnboardingRedirect = outletContext?.setSuppressOnboardingRedirect ?? noop;
  const { merchantId } = useMerchant();
  const { draft } = useOnboardingDraft();
  const {
    data: merchant,
    isSuccess: isMerchantLoaded,
    isError: isMerchantError,
  } = useGetMerchantQuery(merchantId, { skip: !merchantId });
  const [createOnboarding] = useCreateOnboardingMutation();
  const [postAgenticSettings] = usePostAgenticSettingsMutation();
  const [updateMerchant] = useUpdateMerchantMutation();
  const { link: squarespaceStoreLink, isError: isSquarespaceLinkError } = useSquarespaceStoreLink();
  const isSquarespaceLinkSettled =
    draft.store.platformId !== SQUARESPACE_PLATFORM_ID ||
    Boolean(squarespaceStoreLink) ||
    isSquarespaceLinkError;
  const squarespaceStoreLinkRef = React.useRef(squarespaceStoreLink);
  squarespaceStoreLinkRef.current = squarespaceStoreLink;
  const {
    data: voiceProfiles,
    isSuccess: isVoiceProfilesLoaded,
    isError: isVoiceProfilesError,
  } = useGetAgenticVoiceProfilesQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  // Snapshot publish inputs so RTK cache updates during the run do not re-trigger the publish effect.
  const publishInputsRef = React.useRef({
    draft,
    merchantExternalPlatformId: merchant?.externalPlatformId,
    voiceProfileIds: voiceProfiles?.voiceProfileIds,
  });
  publishInputsRef.current = {
    draft,
    merchantExternalPlatformId: merchant?.externalPlatformId,
    voiceProfileIds: voiceProfiles?.voiceProfileIds,
  };
  const [phase, setPhase] = React.useState<ModalPhase>('launching');
  const [activeLaunchStepIndex, setActiveLaunchStepIndex] = React.useState(0);
  const [launchComplete, setLaunchComplete] = React.useState(false);
  const [shopifyWarning, setShopifyWarning] = React.useState<string | undefined>();
  const [bigcommerceWarning, setBigcommerceWarning] = React.useState<string | undefined>();
  const [errorMessage, setErrorMessage] = React.useState('');
  const [publishAttempt, setPublishAttempt] = React.useState(0);
  const [publishResult, setPublishResult] = React.useState<OnboardingPublishResult | null>(null);
  const continuingAfterSuccessRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      if (continuingAfterSuccessRef.current) {
        continuingAfterSuccessRef.current = false;
        return undefined;
      }
      setPhase('launching');
      setActiveLaunchStepIndex(0);
      setLaunchComplete(false);
      setShopifyWarning(undefined);
      setBigcommerceWarning(undefined);
      setErrorMessage('');
      setPublishResult(null);
      setPublishAttempt(0);
      setSuppressOnboardingRedirect(false);
      return undefined;
    }

    setSuppressOnboardingRedirect(true);
    setPhase('launching');
    setActiveLaunchStepIndex(0);
    setLaunchComplete(false);
    setShopifyWarning(undefined);
    setBigcommerceWarning(undefined);
    setErrorMessage('');
    setPublishResult(null);

    const launchTimers: number[] = [];
    PUBLISH_LAUNCH_STEPS.forEach((_, index) => {
      if (index === 0) {
        return;
      }
      launchTimers.push(
        window.setTimeout(() => {
          setActiveLaunchStepIndex(index);
        }, PUBLISH_LAUNCH_STEP_DELAY_MS * index),
      );
    });

    launchTimers.push(
      window.setTimeout(() => {
        setLaunchComplete(true);
      }, PUBLISH_LAUNCH_STEP_DELAY_MS * PUBLISH_LAUNCH_STEPS.length),
    );

    return () => {
      launchTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [open, publishAttempt, setSuppressOnboardingRedirect]);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    if (!merchantId) {
      setErrorMessage('Merchant context is required to publish.');
      setPhase('error');
      return undefined;
    }

    if ((!isMerchantLoaded && !isMerchantError) || !isSquarespaceLinkSettled) {
      return undefined;
    }

    if (!isVoiceProfilesLoaded && !isVoiceProfilesError) {
      return undefined;
    }

    let cancelled = false;

    const runPublish = async () => {
      const {
        draft: publishDraft,
        merchantExternalPlatformId,
        voiceProfileIds,
      } = publishInputsRef.current;

      const result = await executeOnboardingPublish({
        draft: publishDraft,
        merchantId,
        merchantExternalPlatformId,
        squarespaceStoreLink: squarespaceStoreLinkRef.current,
        voiceProfileIds,
        updateMerchant: (body) => updateMerchant({ merchantId, body }),
        createOnboarding: (body) => createOnboarding({ merchantId, body }),
        postAgenticSettings: (body) => postAgenticSettings({ merchantId, body }),
      });

      if (cancelled) {
        return;
      }

      if (result.status === 'error') {
        setErrorMessage(result.message);
        setPhase('error');
        return;
      }

      setPublishResult(result);
    };

    void runPublish();

    return () => {
      cancelled = true;
    };
    // Mutations omitted: including them re-runs publish when RTK cache updates mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    merchantId,
    publishAttempt,
    isMerchantLoaded,
    isMerchantError,
    isVoiceProfilesLoaded,
    isVoiceProfilesError,
    isSquarespaceLinkSettled,
  ]);

  React.useEffect(() => {
    if (!open || !launchComplete || !publishResult || publishResult.status !== 'success') {
      return undefined;
    }

    const successTimer = window.setTimeout(() => {
      setShopifyWarning(publishResult.shopifyWarning);
      setBigcommerceWarning(publishResult.bigcommerceWarning);
      setPhase('success');
    }, PUBLISH_SUCCESS_TRANSITION_DELAY_MS);

    return () => {
      window.clearTimeout(successTimer);
    };
  }, [open, launchComplete, publishResult]);

  const completedLaunchSteps = launchComplete
    ? PUBLISH_LAUNCH_STEPS.length
    : activeLaunchStepIndex + 1;
  const launchProgress = Math.round((completedLaunchSteps / PUBLISH_LAUNCH_STEPS.length) * 100);
  const shopifyQueued = publishResult?.status === 'success' && Boolean(publishResult.shopifyQueued);
  const bigcommerceQueued =
    publishResult?.status === 'success' && Boolean(publishResult.bigcommerceQueued);
  const platformWarning = shopifyWarning ?? bigcommerceWarning;
  const publishSuccess = getOnboardingPlatform(draft.store.platformId).publishSuccess;

  const handleContinue = () => {
    continuingAfterSuccessRef.current = true;
    setSuppressOnboardingRedirect(true);
    onPublishSuccess?.();
    navigate(publishSuccess.continuePath, { replace: true });
  };

  const handleRetry = () => {
    setPublishAttempt((count) => count + 1);
  };

  return (
    <Dialog
      open={open}
      onClose={phase === 'error' ? onClose : undefined}
      disableEscapeKeyDown={phase !== 'error'}
      data-testid="ella-onboarding-publish-modal"
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: '100%',
          maxWidth: 560,
          px: 4,
          py: 5,
        },
      }}
    >
      {phase === 'launching' ? (
        <Box data-testid="ella-onboarding-publish-modal-launching">
          <Typography variant="xl" fontWeight="semibold" sx={{ mb: 2 }}>
            Saving your agent ...
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
            {PUBLISH_LAUNCH_STEPS.map((label, index) => {
              const status = getLaunchStepStatus(index, activeLaunchStepIndex, launchComplete);
              return (
                <Box
                  key={label}
                  data-testid={`ella-onboarding-publish-launch-step-${index}`}
                  data-status={status}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '8px',
                    ...launchRowSx(status),
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      bgcolor:
                        status === 'complete'
                          ? 'success.main'
                          : status === 'in_progress'
                            ? 'warning.main'
                            : 'transparent',
                      border: status === 'pending' ? '1px solid' : 'none',
                      borderColor: status === 'pending' ? 'border.main' : undefined,
                    }}
                  >
                    {status === 'complete' ? (
                      <CheckIcon sx={{ fontSize: 16, color: 'common.white' }} aria-hidden />
                    ) : null}
                    {status === 'in_progress' ? (
                      <CircularProgress size={14} thickness={6} sx={{ color: 'common.white' }} />
                    ) : null}
                  </Box>
                  <Typography
                    variant="medium"
                    fontWeight={status === 'pending' ? 'regular' : 'semibold'}
                  >
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <LinearProgress
            variant="determinate"
            value={launchProgress}
            data-testid="ella-onboarding-publish-launch-progress"
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: 'secondary.light',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                bgcolor: launchComplete ? 'success.main' : 'warning.main',
              },
            }}
          />
        </Box>
      ) : null}

      {phase === 'error' ? (
        <Box data-testid="ella-onboarding-publish-modal-error">
          <Typography variant="xl" fontWeight="semibold" sx={{ mb: 2 }}>
            Couldn&apos;t publish your agent
          </Typography>
          <Typography variant="medium" color="text.secondary" sx={{ mb: 3, lineHeight: '22px' }}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              theme="secondary"
              text="Dismiss"
              onClick={onClose}
              dataTestId="ella-onboarding-publish-dismiss"
              sx={{ minWidth: 120, height: 48 }}
            />
            <Button
              theme="primary"
              text="Try again"
              onClick={handleRetry}
              dataTestId="ella-onboarding-publish-retry"
              sx={{ minWidth: 120, height: 48 }}
            />
          </Box>
        </Box>
      ) : null}

      {phase === 'success' ? (
        <Box
          data-testid="ella-onboarding-publish-modal-success"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            px: 1,
          }}
        >
          <Typography component="h2" variant="xxl" sx={{ mb: 2 }}>
            {PUBLISH_SUCCESS_HEADLINE}
          </Typography>

          <Typography
            variant="medium"
            color="text.secondary"
            sx={{ mb: platformWarning ? 2 : 3, lineHeight: '22px' }}
          >
            {publishSuccess.body}
          </Typography>

          {platformWarning ? (
            <Alert
              severity="warning"
              data-testid="ella-onboarding-publish-platform-warning"
              sx={{ width: '100%', mb: 3, textAlign: 'left' }}
            >
              {platformWarning}
            </Alert>
          ) : null}

          <Typography
            variant="medium"
            fontWeight="semibold"
            sx={{ alignSelf: 'flex-start', mb: 2 }}
          >
            What&apos;s done
          </Typography>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              mb: 3,
              textAlign: 'left',
            }}
          >
            {(shopifyQueued || bigcommerceQueued
              ? [
                  ...PUBLISH_SUCCESS_COMPLETED_STEPS,
                  ...(shopifyQueued ? [SHOPIFY_ONBOARDING_QUEUED_COMPLETED_STEP] : []),
                  ...(bigcommerceQueued ? [BIGCOMMERCE_ONBOARDING_QUEUED_COMPLETED_STEP] : []),
                ]
              : PUBLISH_SUCCESS_COMPLETED_STEPS
            ).map((label, index) => (
              <Box
                key={label}
                data-testid={`ella-onboarding-publish-success-completed-${index}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.25 }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 16, color: 'common.white' }} aria-hidden />
                </Box>
                <Typography variant="medium" fontWeight="medium">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography
            variant="medium"
            fontWeight="semibold"
            sx={{ alignSelf: 'flex-start', mb: 2 }}
          >
            Pending next steps
          </Typography>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              mb: 4,
              textAlign: 'left',
            }}
          >
            {publishSuccess.nextSteps.map((label, index) => (
              <Box
                key={label}
                data-testid={`ella-onboarding-publish-success-next-${index}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.25 }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'border.main',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="medium" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Button
            theme="primary"
            text={publishSuccess.continueLabel}
            onClick={handleContinue}
            dataTestId="ella-onboarding-publish-continue"
            sx={{ minWidth: 260, height: 48 }}
          />

          <Typography
            data-testid="ella-onboarding-publish-support-text"
            variant="small"
            color="text.secondary"
            sx={{ mt: 2, maxWidth: 360, lineHeight: '18px', textAlign: 'center' }}
          >
            If you need help, or are not sure what to do next, please contact{' '}
            <Link href="mailto:support@skipify.com" variant="small">
              support@skipify.com
            </Link>
          </Typography>
        </Box>
      ) : null}
    </Dialog>
  );
};
